import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
  CircleDot,
  Layers,
  Activity,
  Scissors,
  Eye,
  Users,
  Zap,
  BedDouble,
  AlertTriangle,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/neurofibroma-removal-mumbai";
const LIPOMA_PATH = "/lipoma-removal-surgery-mumbai";
const CYST_PATH = "/sebaceous-cyst-removal-mumbai";
const KELOID_PATH = "/keloid-treatment-mumbai";

const NEURO_FAQS = [
  {
    question: "Is a neurofibroma dangerous?",
    answer:
      "In the vast majority of cases, no. Solitary neurofibromas and the cutaneous neurofibromas of neurofibromatosis type 1 are benign and do not become malignant. A small percentage of plexiform neurofibromas in NF1 patients can transform into malignant peripheral nerve sheath tumors, which is a rare but serious complication. Any neurofibroma growing rapidly, developing pain, or becoming firm requires prompt specialist assessment regardless of previous diagnosis.",
  },
  {
    question: "Can a neurofibroma be left without treatment?",
    answer:
      "Yes, for asymptomatic, stable neurofibromas that are not causing functional or significant cosmetic concern. Observation with periodic review is an entirely appropriate management approach. The decision to remove a neurofibroma is based on the patient's symptoms, concern, and preference rather than a requirement to treat all neurofibromas surgically.",
  },
  {
    question: "Will a neurofibroma grow back after removal?",
    answer:
      "Recurrence after complete surgical excision is uncommon for solitary cutaneous and subcutaneous neurofibromas. In patients with neurofibromatosis type 1, removing one lesion does not affect the others or prevent new ones from developing, as the underlying genetic condition continues to cause new neurofibromas to form throughout the patient's life.",
  },
  {
    question: "How do I know if I have neurofibromatosis type 1?",
    answer:
      "NF1 is diagnosed when a patient has at least two of the following features: multiple neurofibromas, six or more café-au-lait spots larger than 5 millimeters, axillary or groin freckling, Lisch nodules in the eye, a first-degree relative with NF1, a characteristic bone lesion, or an optic pathway glioma. If you have multiple soft skin nodules alongside café-au-lait spots or a family history of similar findings, assessment for NF1 is appropriate before any surgical planning.",
  },
  {
    question: "Will there be a scar after neurofibroma removal?",
    answer:
      "Yes, a small linear scar remains at the incision site. Dr. Jain places incisions along natural skin lines and uses layered fine suture closure to minimize scar width and visibility. Most neurofibroma removal scars on the face and body fade to near-invisible within 6 to 12 months with appropriate scar care. Earlier removal of smaller lesions produces smaller incisions and less visible scars.",
  },
  {
    question: "How is neurofibroma removal different from lipoma removal?",
    answer:
      "Both are removed through surgical excision under local anesthesia and the procedures look similar from the patient's perspective. The key technical difference is that neurofibromas arise from nerve sheath cells and have a relationship with the associated peripheral nerve that must be managed carefully during dissection. Lipomas are fatty tumors with no nerve association and are dissected more straightforwardly. Histological analysis distinguishes the two when clinical diagnosis is uncertain.",
  },
  {
    question: "Can multiple neurofibromas be removed at the same time?",
    answer:
      "Yes, within the limits of what local anesthesia and procedure duration safely allow in a single session. For patients with NF1 and many lesions, Dr. Jain plans a prioritized serial removal approach, addressing the most significant lesions first and subsequent ones in planned later sessions. The specific lesions to prioritize are decided based on size, location, cosmetic impact, and functional concern.",
  },
  {
    question: "Is neurofibroma removal painful?",
    answer:
      "The procedure is performed under local anesthesia. The injection produces brief discomfort. The removal itself is painless. Post-procedure mild soreness and swelling for 2 to 3 days is managed with standard pain medication. Most patients describe the experience as significantly less uncomfortable than they anticipated before the procedure.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Neurofibroma",
      description:
        "Benign peripheral nerve sheath tumor; solitary or associated with neurofibromatosis type 1; managed with excision, selective destructive techniques, observation, or staged removal.",
      associatedAnatomy: { "@type": "AnatomicalStructure", name: "Peripheral nerve sheath" },
      possibleTreatment: ["Surgical excision", "Radiofrequency removal", "Observation"],
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Neurofibroma surgical excision",
      description:
        "Local anesthesia excision of benign nerve sheath tumor with nerve-preserving dissection and histological confirmation under plastic surgical care.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Skin and subcutaneous tissue" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "MedicalCondition",
      name: "Neurofibromatosis type 1",
      alternateName: ["NF1", "von Recklinghausen disease"],
      description:
        "Autosomal dominant disorder with multiple neurofibromas, café-au-lait macules, and systemic features requiring long-term specialist monitoring; related assessment when multiple neurofibromas are present.",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: NEURO_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function NeurofibromaJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Neurofibroma Removal", href: "/neurofibroma-removal-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Skin &amp; Soft Tissue</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-5">Neurofibroma Removal</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg text-deep-navy/70 mb-8">
              Benign Nerve Tumor Removal by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Neurofibroma" icon="🔬" size="hero"  imageSrc={HERO.neurofibromaRemoval} imageAlt="Neurofibroma removal"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is a Neurofibroma?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A neurofibroma is a benign tumor arising from the cells surrounding peripheral nerves. It is not cancer and does not invade surrounding tissue in the way malignant tumors do. Neurofibromas appear as soft, rubbery, skin-colored nodules on or just beneath the skin surface. They may be solitary, occurring as a single isolated growth in an otherwise healthy person, or multiple, occurring as part of a genetic condition called neurofibromatosis type 1.
            </p>
            <p>
              Solitary neurofibromas are common and typically cause no medical concern beyond their physical presence. They grow slowly, are soft and slightly compressible on examination, and may cause mild tingling or pressure discomfort when pressed. Multiple neurofibromas occurring alongside other specific findings, particularly café-au-lait skin spots and a family history of the same, require evaluation for neurofibromatosis type 1, a hereditary condition that requires ongoing specialist monitoring.
            </p>
            <p>
              Dr. Vicky Jain removes neurofibromas through surgical excision under local anesthesia for solitary or selected lesions in patients with neurofibromatosis. The excised specimen is sent for histological analysis to confirm the diagnosis. For patients with neurofibromatosis type 1, removal of specific cosmetically or functionally troublesome lesions is planned as part of a broader long-term management discussion.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Neurofibroma Treatment</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The first priority in any neurofibroma consultation is confirming the diagnosis and assessing whether the patient has a solitary lesion or multiple lesions that warrant evaluation for neurofibromatosis type 1. A single soft nodule in an otherwise healthy adult with no family history and no café-au-lait spots is almost certainly a solitary neurofibroma and is managed straightforwardly with excision if the patient wants it removed. A patient with multiple lesions requires a broader assessment before deciding which lesions to remove and when.
              </p>
              <p>
                For surgical removal, the key technical consideration is the relationship between the neurofibroma and the nerve from which it arises. Superficial cutaneous neurofibromas are not closely associated with a specific named nerve and can be removed completely without nerve risk. Deeper subcutaneous and plexiform neurofibromas involve the nerve more intimately, and excision requires careful dissection to remove as much of the tumor as possible while preserving nerve function. Complete removal without nerve sacrifice is the goal for all but the most extensive plexiform tumors.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TypesSection() {
  const cards = [
    {
      title: "Cutaneous Neurofibroma",
      icon: CircleDot,
      copy: 'The most common type. Soft, button-like nodules on or just beneath the skin surface. Can be moved slightly with a finger. The characteristic "buttonhole sign" describes the way a cutaneous neurofibroma can be invaginated through the skin with finger pressure. Usually less than 2 centimeters in diameter. Easily removed surgically with minimal complication risk.',
    },
    {
      title: "Subcutaneous Neurofibroma",
      icon: Layers,
      copy: "Located deeper beneath the skin along the course of peripheral nerves. Firmer than cutaneous neurofibromas. May cause local pain or tingling when pressed due to proximity to the nerve. Imaging is helpful before surgery to define the extent and nerve relationship before planning excision.",
    },
    {
      title: "Plexiform Neurofibroma",
      icon: Activity,
      copy: "Large, diffuse tumors involving multiple nerve fascicles or an entire nerve plexus. Described as feeling like a bag of worms under the skin. More complex to treat because complete removal without significant nerve damage is often not achievable. More common in patients with neurofibromatosis type 1. Require specialist multidisciplinary assessment before any surgical intervention.",
    },
    {
      title: "Solitary Neurofibroma",
      icon: Eye,
      copy: "A single neurofibroma in a patient without neurofibromatosis. Common in the general population. No increased risk of becoming malignant. Managed by observation or elective removal depending on size, location, and patient preference. No systemic evaluation required in the absence of other features.",
    },
    {
      title: "Multiple Neurofibromas in NF1",
      icon: Users,
      copy: "Patients with neurofibromatosis type 1 develop multiple neurofibromas, often numbering in the dozens to hundreds, across the skin. Individual troublesome lesions are removed selectively. Removing all lesions in NF1 is not practical or appropriate. Management is planned with an understanding of which lesions are causing the most significant functional or cosmetic problem.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Neurofibroma</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_LIPOMA_ROWS = [
  {
    label: "Origin",
    neuro: "Arises from nerve sheath cells surrounding peripheral nerves.",
    lipoma: "Arises from adipose (fat) tissue beneath the skin.",
  },
  {
    label: "Texture",
    neuro: "Soft but with a slightly firmer, more rubbery quality. Subcutaneous types are firmer.",
    lipoma: "Soft and doughy. Compresses easily.",
  },
  {
    label: "Pain or Tingling",
    neuro: "Sometimes. Pressing on a neurofibroma may cause tingling if the associated nerve is stimulated.",
    lipoma: "Usually painless. Occasionally tender if pressing on a nerve.",
  },
  {
    label: "Buttonhole Sign",
    neuro: "Present in cutaneous type. The nodule can be invaginated through the skin with finger pressure.",
    lipoma: "Absent.",
  },
  {
    label: "Association with Genetic Condition",
    neuro: "Multiple neurofibromas are associated with neurofibromatosis type 1.",
    lipoma: "Multiple lipomas associated with familial multiple lipomatosis, a separate condition.",
  },
  {
    label: "Histological Confirmation",
    neuro: "Recommended. Confirms nerve sheath origin and excludes rare malignant variants.",
    lipoma: "Recommended for any atypical presentation.",
  },
];

function VsLipomaSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="vs-lipoma-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Diagnosis</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Neurofibroma vs. Lipoma</h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Neurofibroma
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  <Link to={LIPOMA_PATH} className="hover:text-gold transition-colors">
                    Lipoma
                  </Link>
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_LIPOMA_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.neuro}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.lipoma}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          Clinical distinction between a neurofibroma and a lipoma is not always possible on examination alone. Both are soft subcutaneous nodules. Ultrasound can provide useful information about the internal structure. Histological analysis of the excised specimen confirms the diagnosis definitively.
        </p>
      </div>
    </section>
  );
}

function Nf1Section() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="nf1-section" aria-labelledby="nf1-heading">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">NF1 Context</p>
        <h2 id="nf1-heading" className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
          Neurofibromatosis Type 1: When Multiple Neurofibromas Mean More
        </h2>
        <Alert className="rounded-none border-2 border-gold/50 bg-white max-w-4xl mx-auto py-6 px-4 md:px-6">
          <AlertTriangle className="h-5 w-5 text-gold" aria-hidden />
          <AlertTitle className="font-serif text-lg text-premium-purple pl-7 mb-2">Important clinical context</AlertTitle>
          <AlertDescription className="pl-7 text-deep-navy/80 space-y-6">
            <p className="leading-relaxed">
              Neurofibromatosis type 1 (NF1) is a genetic condition caused by a mutation in the NF1 gene. It is one of the most common genetic disorders, affecting approximately 1 in 3,000 people worldwide. NF1 is characterized by the development of multiple neurofibromas, café-au-lait spots, axillary and groin freckling, Lisch nodules in the eye, and in some cases bone abnormalities and learning difficulties. It is diagnosed when a patient has at least two of these features, one of which is typically multiple neurofibromas.
            </p>
            <p className="leading-relaxed">
              Patients with NF1 require long-term specialist monitoring because the condition affects multiple organ systems and because a small percentage of plexiform neurofibromas in NF1 can transform into malignant peripheral nerve sheath tumors, a serious complication that requires urgent treatment. Any neurofibroma in an NF1 patient that changes rapidly in size, becomes painful, or develops a firm, fixed quality requires prompt assessment rather than observation. The surgical removal of individual neurofibromas in NF1 is appropriate for lesions causing significant cosmetic or functional problems but does not treat the underlying condition.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </section>
  );
}

const TREATMENT_OPTIONS = [
  {
    title: "Surgical Excision",
    icon: Scissors,
    body:
      "The gold standard for neurofibroma removal. The lesion is excised through a small incision with careful dissection to separate the neurofibroma from the surrounding tissue and the associated nerve where possible. The specimen is sent for histological analysis. Results are permanent for the excised lesion. Cutaneous neurofibromas are removed under local anesthesia in a clinic setting. Subcutaneous and plexiform neurofibromas may require more extensive planning and a theatre setting depending on their size and nerve involvement.",
  },
  {
    title: "Radiofrequency and Laser Removal",
    icon: Zap,
    body:
      "Appropriate for small, superficial cutaneous neurofibromas where the primary concern is cosmetic and the lesion is clearly identified. Produces minimal scarring. Does not provide a specimen for histological analysis, which is a limitation for lesions where diagnostic confirmation is important. Used selectively for confirmed small cutaneous neurofibromas in appropriate locations.",
  },
  {
    title: "Serial Removal",
    icon: Activity,
    body:
      "For patients with neurofibromatosis type 1 and multiple lesions, surgical removal of selected lesions is staged across multiple sessions. The lesions causing the most significant cosmetic or functional problem are prioritized. Planning takes into account healing time between sessions, the total number of lesions to be addressed, and the patient's priorities and preferences.",
  },
  {
    title: "Observation",
    icon: Eye,
    body:
      "Appropriate for asymptomatic, stable neurofibromas without cosmetic concern or functional impairment. Regular clinical review monitors for any change in size or character that would change the recommendation. Observation is not the same as ignoring the lesion. It is an active plan with defined criteria for when to proceed to treatment.",
  },
];

function TreatmentOptionsSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Neurofibroma Removal Treatment Options</h2>
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-8 max-w-4xl mx-auto">
          {TREATMENT_OPTIONS.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0" aria-hidden>
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
                  <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Neurofibroma Removal Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 20 to 40 minutes for small to medium lesions under local anesthesia. Larger or deeper lesions may require longer and a different anesthesia approach. No hospital admission is required for most cutaneous and small subcutaneous neurofibromas. Dr. Jain marks the planned incision before surgery, oriented along natural skin lines for the most cosmetically favorable scar.
            </p>
            <p>
              An incision is made over the neurofibroma. The lesion is dissected from the surrounding tissue with care to identify and preserve the associated nerve where possible. For cutaneous neurofibromas the nerve association is superficial and the dissection is straightforward. For subcutaneous neurofibromas the nerve relationship is more intimate and dissection is performed under magnification to minimize the risk of nerve injury. The lesion is removed, the wound is closed in layers with fine sutures, and a dressing is applied.
            </p>
            <p>
              The excised specimen is sent for histological analysis at every case. Results confirming neurofibroma are reviewed at the post-operative visit. In the rare case where histology reveals unexpected findings, further management is discussed with the patient at that visit.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild swelling and discomfort at the removal site. Normal and expected. Standard pain medication manages any discomfort. A simple dressing covers the wound.",
      icon: BedDouble,
    },
    {
      period: "Days 7 to 10",
      description:
        "Sutures are removed at the post-operative visit. The wound is assessed. Histological results are reviewed. The removal site is healed or close to healed.",
      icon: Monitor,
    },
    {
      period: "Week 2",
      description:
        "Normal healing complete for most patients. Full normal activity has resumed. Scar care begins to optimize the long-term appearance.",
      icon: Clock,
    },
    {
      period: "Months 1 to 6",
      description:
        "The scar matures and fades. Most neurofibroma removal scars are small and linear and fade to near-invisible within 6 months on the face and body with appropriate scar care.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoShouldConsiderSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-should-consider-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Who Should Consider Neurofibroma Removal?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any patient with a confirmed or suspected neurofibroma that is causing cosmetic concern, pain, tingling, or functional impairment is appropriate for consultation. There is no minimum size. A small but cosmetically prominent neurofibroma on the face in an otherwise healthy patient is a valid indication for elective removal. Asymptomatic, stable neurofibromas that are not causing concern can be observed rather than removed if the patient prefers this approach.
            </p>
            <p>
              Patients with multiple neurofibromas should be assessed for neurofibromatosis type 1 before planning surgery on individual lesions. This assessment determines whether the lesions represent a sporadic multiple occurrence or NF1, which affects the long-term management plan and monitoring requirements. Patients with known NF1 who want specific troublesome lesions removed should discuss which lesions take priority and how removal fits into their overall NF1 management. Any neurofibroma that is growing rapidly, becoming painful, or developing firmness requires prompt assessment rather than elective booking, as these features can indicate rare malignant transformation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Lipoma" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {NEURO_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`neuro-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Neurofibroma Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain examines the lesion, assesses whether evaluation for neurofibromatosis type 1 is needed, and discusses whether removal, observation, or a staged approach is appropriate for your specific situation. Histological analysis is arranged for every excised specimen. You leave with a clear diagnosis, a surgical plan if removal is indicated, and an understanding of any long-term monitoring that may be relevant.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Lipoma Removal",
      description: "Removal of benign fatty tumors that are sometimes confused with subcutaneous neurofibromas.",
      href: LIPOMA_PATH,
    },
    {
      name: "Sebaceous Cyst Removal",
      description: "Assessment and removal of skin growths evaluated alongside neurofibroma assessment.",
      href: CYST_PATH,
    },
    {
      name: "Keloid Treatment",
      description: "Managing abnormal scar formation after neurofibroma removal in predisposed patients.",
      href: KELOID_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function NeurofibromaRemovalPage() {
  return (
    <>
      <SEO
        title="Neurofibroma Removal | Dr. Vicky Jain"
        description="Soft nerve tumors causing cosmetic concern, pain, or tingling. Dr. Vicky Jain performs safe neurofibroma removal with minimal scarring. Book today."
        canonical="/neurofibroma-removal-mumbai"
      />
      <NeurofibromaJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <TypesSection />
      <VsLipomaSection />
      <Nf1Section />
      <TreatmentOptionsSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <WhoShouldConsiderSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
